// Stage 5.0 (2026-05-25) — per-country visa pathway detail dataset.
//
// Loaded from /data/country_visa_extra.json (static, produced by research agents).
// Used by: scoreVisa() (Phase 3), AI helper context, next-steps page.
//
// Key design: `scoreVisaPathway()` is a pure function — takes the data file
// and user answers, returns a 0–1 score. This lets us call it from scoreCountry()
// without coupling scoring.ts to React hooks.

import { useQuery } from '@tanstack/react-query';
import type { VisaPathway } from './types';

// ─── Schema types ─────────────────────────────────────────────────────────────

export interface VisaPathwayDetail {
  available: boolean;
  program_name_en?: string;
  program_name_local?: string;
  /** Minimum monthly income proof in EUR (for remote worker / freelance visas). */
  min_income_eur_month?: number;
  /** Minimum investment in EUR (for golden visa). */
  min_investment_eur?: number;
  investment_types?: string[];
  duration_initial_years?: number;
  renewable?: boolean;
  path_to_pr?: boolean;
  /** Years of legal residence until permanent residency eligibility. */
  pr_years?: number;
  path_to_citizenship?: boolean;
  /** Years from first permit or PR until citizenship eligibility. */
  citizenship_years?: number;
  /** Can RU/BY/UA passport holders apply under this pathway? */
  ru_by_ua_eligible?: boolean;
  /** Specific restriction note for RU/BY/UA holders. */
  ru_by_ua_notes?: string;
  source_url?: string;
  notes_en: string;
  notes_ru: string;
  last_checked?: string;
}

export type VisaPathwayKey =
  | 'digital_nomad'
  | 'freelance_self_employed'
  | 'golden_investor'
  | 'family_reunion'
  | 'ancestry'
  | 'work_sponsor'
  | 'student_to_perm'
  | 'refugee_protection';

export type CountryVisaDetail = Partial<Record<VisaPathwayKey, VisaPathwayDetail>>;

export interface CountryVisaExtraFile {
  _meta: {
    schema_version: number;
    generated: string;
    countries_covered: string[];
    disclaimer?: string;
  };
  countries: Record<string, CountryVisaDetail>;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

export function useCountryVisaExtra() {
  return useQuery({
    queryKey: ['country-visa-extra'],
    queryFn: async (): Promise<CountryVisaExtraFile> => {
      const res = await fetch('/data/country_visa_extra.json');
      if (!res.ok) throw new Error(`country_visa_extra.json: ${res.status}`);
      return res.json();
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

// ─── Scoring helper ───────────────────────────────────────────────────────────

/**
 * Map quiz `visa_pathway` option IDs to the visa-extra dataset keys.
 * `unknown_exploring` has no specific pathway — treated as neutral.
 */
const QUIZ_TO_KEY: Record<VisaPathway, VisaPathwayKey | null> = {
  digital_nomad: 'digital_nomad',
  freelance_self_employed: 'freelance_self_employed',
  golden_investor: 'golden_investor',
  family_reunion: 'family_reunion',
  ancestry: 'ancestry',
  work_sponsor: 'work_sponsor',
  student_to_perm: 'student_to_perm',
  refugee_protection: 'refugee_protection',
  unknown_exploring: null,
};

export interface VisaScoreInput {
  /** Country code (e.g. "PT", "IT"). */
  countryCode: string;
  /** Visa pathways the user selected (from quiz answer). */
  selectedPathways: VisaPathway[];
  /** User passport (ru/by/ua holders may face restrictions). */
  passport?: string;
  /** Whether the user is moving urgently (horizon = "now"). */
  urgent?: boolean;
}

/**
 * Score how well a country's visa options match the user's chosen pathways.
 *
 * Returns 0–1. When `file` is not yet loaded, returns null so the caller
 * can fall back to the legacy metrics.visa boolean flags.
 *
 * Scoring logic:
 * - If ANY selected pathway is available & user is eligible → high score
 * - Fraction of pathways available boosts the score further
 * - RU/BY/UA restriction on an available pathway → modest penalty
 * - `unknown_exploring` selected → neutral 0.6 (no specific need)
 */
export function scoreVisaPathway(
  file: CountryVisaExtraFile | undefined,
  input: VisaScoreInput,
): number | null {
  if (!file) return null;

  const entry = file.countries[input.countryCode.toUpperCase()];
  if (!entry) return null; // Country not in dataset — caller falls back

  const { selectedPathways, passport, urgent } = input;

  // No specific pathway selected (only unknown_exploring)
  const meaningful = selectedPathways.filter((p) => p !== 'unknown_exploring');
  if (meaningful.length === 0) return 0.6; // Neutral: user hasn't committed

  let bestScore = 0;
  let availableCount = 0;

  for (const pathway of meaningful) {
    const key = QUIZ_TO_KEY[pathway];
    if (!key) continue;
    const detail = entry[key];
    if (!detail) {
      // No data for this pathway in this country → treat as unknown (0.4)
      bestScore = Math.max(bestScore, 0.4);
      continue;
    }

    if (!detail.available) {
      // Pathway explicitly unavailable → small floor (0.1), pathway is blocked
      bestScore = Math.max(bestScore, 0.1);
      continue;
    }

    availableCount++;

    // Base: pathway available → 0.7
    let pathScore = 0.7;

    // Quick citizenship path is a strong positive
    if (detail.path_to_citizenship && (detail.citizenship_years ?? 99) <= 6) {
      pathScore += 0.15;
    }
    // Path to PR (without full citizenship) is a moderate positive
    else if (detail.path_to_pr && (detail.pr_years ?? 99) <= 5) {
      pathScore += 0.1;
    }

    // RU/BY/UA restriction
    if (
      (passport === 'ru' || passport === 'by' || passport === 'ua') &&
      detail.ru_by_ua_eligible === false
    ) {
      pathScore *= 0.3; // Hard restriction: massive penalty
    } else if (
      (passport === 'ru' || passport === 'by' || passport === 'ua') &&
      detail.ru_by_ua_notes
    ) {
      pathScore *= 0.8; // Soft restriction note present
    }

    // Urgent move: deprioritise high-investment or long-processing pathways
    if (urgent) {
      if (pathway === 'golden_investor' || pathway === 'student_to_perm') {
        pathScore *= 0.85;
      }
    }

    bestScore = Math.max(bestScore, pathScore);
  }

  // Breadth bonus: if multiple pathways are available, the country is flexible
  if (availableCount >= 2) bestScore = Math.min(1, bestScore + 0.05);
  if (availableCount >= 3) bestScore = Math.min(1, bestScore + 0.05);

  return Math.min(1, Math.max(0, bestScore));
}

// ─── AI context helper ─────────────────────────────────────────────────────────

export interface VisaAiBlock {
  available_pathways: string[];
  top_pathway?: {
    key: string;
    name: string;
    min_income_eur?: number;
    pr_years?: number;
    notes: string;
  };
}

/**
 * Build a compact AI-helper block for the visa situation in a given country.
 * Kept small (<400 bytes) to fit within the 8 KB RAG context budget.
 */
export function buildVisaAiBlock(
  file: CountryVisaExtraFile | undefined,
  countryCode: string,
  selectedPathways: VisaPathway[],
  lang: 'en' | 'ru',
): VisaAiBlock | null {
  if (!file) return null;
  const entry = file.countries[countryCode.toUpperCase()];
  if (!entry) return null;

  const available: string[] = [];
  let topPathway: VisaAiBlock['top_pathway'] | undefined;
  let topScore = -1;

  for (const [key, detail] of Object.entries(entry) as [VisaPathwayKey, VisaPathwayDetail][]) {
    if (!detail.available) continue;
    available.push(key);

    const isSelected = selectedPathways.some((p) => QUIZ_TO_KEY[p] === key);
    if (isSelected && available.length > topScore) {
      topScore = available.length;
      topPathway = {
        key,
        name: detail.program_name_en ?? key,
        min_income_eur: detail.min_income_eur_month,
        pr_years: detail.pr_years,
        notes: lang === 'ru' ? detail.notes_ru : detail.notes_en,
      };
    }
  }

  return {
    available_pathways: available,
    top_pathway: topPathway ?? (available[0]
      ? {
          key: available[0],
          name: entry[available[0] as VisaPathwayKey]?.program_name_en ?? available[0],
          notes: lang === 'ru'
            ? (entry[available[0] as VisaPathwayKey]?.notes_ru ?? '')
            : (entry[available[0] as VisaPathwayKey]?.notes_en ?? ''),
        }
      : undefined),
  };
}
